import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { FiArrowLeft, FiZap, FiMail, FiUser, FiSave, FiEdit3 } from 'react-icons/fi';
import CreditPlans from '../common/CreditPlans';
import Footer from '../Layout/Footer';

export default function UserDashboard() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: user?.name || '',
    emailSubscribed: user?.emailSubscribed ?? true,
    signature: {
      title: user?.signature?.title || '',
      company: user?.signature?.company || '',
      phone: user?.signature?.phone || '',
      customText: user?.signature?.customText || ''
    }
  });
  const [saving, setSaving] = useState(false);
  const [showPlans, setShowPlans] = useState(false);

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await api.put('/auth/profile', { name: form.name, emailSubscribed: form.emailSubscribed, signature: form.signature });
      updateUser({ name: form.name, emailSubscribed: form.emailSubscribed, signature: form.signature });
      toast.success('Profile updated');
    } catch {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <button onClick={() => navigate('/')} style={styles.backBtn}>
          <FiArrowLeft size={18} /> Back to Chat
        </button>

        <h1 style={styles.title}>Account Settings</h1>

        {/* Profile Section */}
        <div style={styles.card}>
          <h2 style={styles.cardTitle}><FiUser size={20} /> Profile</h2>
          <div style={styles.formGroup}>
            <label style={styles.label}>Name</label>
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={styles.input} />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Email</label>
            <input value={user?.email || ''} disabled style={{ ...styles.input, opacity: 0.6 }} />
          </div>
          <label style={styles.checkboxLabel}>
            <input type="checkbox" checked={form.emailSubscribed} onChange={e => setForm({ ...form, emailSubscribed: e.target.checked })} style={styles.checkbox} />
            <span>Receive product updates and tips via email</span>
          </label>
          <button onClick={handleSaveProfile} disabled={saving} style={styles.saveBtn}>
            <FiSave size={16} /> {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>

        {/* Reply Signature */}
        <div style={styles.card}>
          <h2 style={styles.cardTitle}><FiEdit3 size={20} /> Reply Signature</h2>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16, lineHeight: 1.5 }}>
            This signature will be added to the end of your generated replies. Write it exactly how you want it to appear.
          </p>
          <div style={styles.formGroup}>
            <label style={styles.label}>Your Signature</label>
            <textarea
              value={form.signature.customText}
              onChange={e => setForm({ ...form, signature: { ...form.signature, customText: e.target.value } })}
              placeholder={"Very Respectfully,\n\nNilesh Panchal\nCEO | ChatWithDr.com\nsupport@chatwithdr.com"}
              style={{ ...styles.input, minHeight: 120, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6 }}
              rows={5}
            />
          </div>

          {/* Live Preview */}
          {form.signature.customText && (
            <div style={styles.signaturePreview}>
              <span style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>Preview</span>
              <div style={{ marginTop: 8, fontSize: 14, color: 'var(--text-primary)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                {form.signature.customText}
              </div>
            </div>
          )}

          <button onClick={handleSaveProfile} disabled={saving} style={styles.saveBtn}>
            <FiSave size={16} /> {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>

        {/* Credits & Usage */}
        <div style={styles.card}>
          <h2 style={styles.cardTitle}><FiZap size={20} /> Credits & Usage</h2>
          <div style={styles.statsGrid}>
            <div style={styles.statBox}>
              <span style={styles.statValue}>{(user?.credits || 0).toLocaleString()}</span>
              <span style={styles.statLabel}>Credits Remaining</span>
            </div>
            <div style={styles.statBox}>
              <span style={styles.statValue}>{(user?.totalCreditsUsed || 0).toLocaleString()}</span>
              <span style={styles.statLabel}>Credits Used</span>
            </div>
            <div style={styles.statBox}>
              <span style={styles.statValue}>{user?.totalRepliesGenerated || 0}</span>
              <span style={styles.statLabel}>Total Replies</span>
            </div>
          </div>

          <button onClick={() => setShowPlans(true)} style={styles.buyCreditsBtn}>
            <FiZap size={16} /> Buy More Credits
          </button>
        </div>

        {/* Credit Plans */}
        <div style={styles.card}>
          <h2 style={styles.cardTitle}><FiMail size={20} /> Credit Plans</h2>
          <p style={styles.creditDesc}>Buy credits once, use anytime. No subscriptions. No recurring charges.</p>
          <div style={styles.creditBadges}>
            <span style={styles.creditBadge}>1 credit = 1 word generated</span>
            <span style={styles.creditBadge}>Credits never expire</span>
            <span style={styles.creditBadge}>No subscriptions</span>
          </div>
          <div style={styles.planGrid}>
            {[
              { name: 'Starter', credits: '50,000', price: '$5', id: 'starter' },
              { name: 'Pro', credits: '150,000', price: '$10', id: 'pro', popular: true },
              { name: 'Power', credits: '500,000', price: '$25', id: 'power' }
            ].map(plan => (
              <div key={plan.id} style={{ ...styles.planCard, ...(plan.popular ? { borderColor: 'var(--accent)' } : {}) }}>
                {plan.popular && <div style={styles.bestValue}>Best Value</div>}
                <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>{plan.name}</h3>
                <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--accent)' }}>{plan.price}</div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>{plan.credits} credits</div>
                <button onClick={() => setShowPlans(true)} style={styles.planBtn}>Buy</button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Footer />
      <CreditPlans isOpen={showPlans} onClose={() => setShowPlans(false)} currentCredits={user?.credits || 0} />
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: 'var(--bg-primary)', display: 'flex', flexDirection: 'column' },
  container: { maxWidth: 640, margin: '0 auto', padding: '40px 20px', flex: 1 },
  backBtn: { display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: 14, cursor: 'pointer', marginBottom: 24 },
  title: { fontSize: 28, fontWeight: 700, marginBottom: 32, color: 'var(--text-primary)' },
  card: { background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 16, padding: 28, marginBottom: 24 },
  cardTitle: { display: 'flex', alignItems: 'center', gap: 10, fontSize: 18, fontWeight: 700, marginBottom: 20, color: 'var(--text-primary)' },
  formGroup: { marginBottom: 16 },
  label: { display: 'block', fontSize: 13, color: 'var(--text-muted)', marginBottom: 6, fontWeight: 500 },
  input: { width: '100%', padding: '12px 14px', background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 14, outline: 'none' },
  checkboxLabel: { display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-secondary)', fontSize: 14, marginBottom: 20, cursor: 'pointer' },
  checkbox: { width: 18, height: 18, accentColor: 'var(--accent)' },
  saveBtn: { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: 'var(--accent)', color: 'white', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 20 },
  statBox: { background: 'var(--bg-primary)', borderRadius: 12, padding: 20, textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 4 },
  statValue: { fontSize: 28, fontWeight: 700, color: 'var(--accent)' },
  statLabel: { fontSize: 12, color: 'var(--text-muted)' },
  creditDesc: { fontSize: 14, color: 'var(--text-secondary)', marginBottom: 12, lineHeight: 1.5 },
  creditBadges: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 },
  creditBadge: { padding: '4px 12px', background: 'var(--accent-dim)', color: 'var(--accent)', borderRadius: 16, fontSize: 12, fontWeight: 600 },
  buyCreditsBtn: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', padding: '12px', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: 'white', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer' },
  planGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 },
  planCard: { background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 12, padding: 20, textAlign: 'center', position: 'relative' },
  bestValue: { position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)', background: 'var(--accent)', color: 'white', padding: '2px 10px', borderRadius: 10, fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap' },
  planBtn: { width: '100%', padding: '8px', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  signaturePreview: { background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginBottom: 20 }
};
